import { module as BaseModule } from "modujs";

const PROJECT_FILTER_UPDATED_EVENT = "project-filter:updated";

export default class extends BaseModule {
  init() {
    this.toggleButton = this.el.querySelector("[data-filter-toggle]");
    this.menu = this.el.querySelector("[data-filter-menu]");
    this.optionButtons = Array.from(this.el.querySelectorAll("[data-filter-option]"));
    this.slider = document.querySelector("[data-module-slider]");
    this.grid = document.querySelector("[data-filter-grid]");
    this.gridItems = Array.from(document.querySelectorAll("[data-filter-grid-item]"));
    this.gridEmptyState = document.querySelector("[data-filter-grid-empty]");
    this.activeFilter = null;
    this.isOpen = false;

    if (!this.toggleButton || !this.menu || this.optionButtons.length === 0) return;

    this.onToggleClick = this.handleToggleClick.bind(this);
    this.onOptionClick = this.handleOptionClick.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);

    this.toggleButton.addEventListener("click", this.onToggleClick);
    this.optionButtons.forEach((button) => button.addEventListener("click", this.onOptionClick));
    document.addEventListener("click", this.onDocumentClick);

    this.setActiveFilter(null);
  }

  destroy() {
    this.toggleButton?.removeEventListener("click", this.onToggleClick);
    this.optionButtons?.forEach((button) => button.removeEventListener("click", this.onOptionClick));
    document.removeEventListener("click", this.onDocumentClick);

    // Restore smooth scroll when leaving the page/module.
    if (window.lenis?.start) {
      window.lenis.start();
    }
  }

  handleToggleClick() {
    this.isOpen ? this.closeMenu() : this.openMenu();
  }

  handleOptionClick(event) {
    const button = event.currentTarget;
    const filterValue = button.dataset.filterValue || null;
    if (filterValue && filterValue === this.activeFilter) {
      this.setActiveFilter(null);
    } else {
      this.setActiveFilter(filterValue);
    }
  }

  handleDocumentClick(event) {
    if (!this.isOpen) return;
    if (this.el.contains(event.target)) return;
    this.closeMenu();
  }

  openMenu() {
    this.isOpen = true;
    this.toggleButton.setAttribute("aria-expanded", "true");
    this.menu.classList.remove("hidden");
    this.menu.setAttribute("aria-hidden", "false");
  }

  closeMenu() {
    this.isOpen = false;
    this.toggleButton.setAttribute("aria-expanded", "false");
    this.menu.classList.add("hidden");
    this.menu.setAttribute("aria-hidden", "true");
  }

  setActiveFilter(filterValue) {
    this.activeFilter = filterValue;

    this.optionButtons.forEach((button) => {
      const isActive = button.dataset.filterValue === this.activeFilter;
      button.setAttribute("aria-pressed", String(isActive));
    });

    this.updateLayouts();
    this.emitUpdate();
  }

  updateLayouts() {
    const hasActiveFilter = Boolean(this.activeFilter);

    if (this.slider) {
      this.slider.classList.toggle("hidden", hasActiveFilter);
      this.slider.setAttribute("aria-hidden", String(hasActiveFilter));
      this.slider.style.display = hasActiveFilter ? "none" : "";
    }

    if (this.grid) {
      this.grid.classList.toggle("hidden", !hasActiveFilter);
      this.grid.setAttribute("aria-hidden", String(!hasActiveFilter));
      this.grid.style.display = hasActiveFilter ? "" : "none";
    }

    // Let the native overflow container handle scroll while filter grid is active.
    if (window.lenis) {
      if (hasActiveFilter && window.lenis.stop) {
        window.lenis.stop();
      } else if (!hasActiveFilter && window.lenis.start) {
        window.lenis.start();
      }
    }

    if (!this.gridItems.length) return;

    let visibleCount = 0;

    this.gridItems.forEach((item) => {
      const termSlugs = (item.dataset.filterTerms || "").split(" ").filter(Boolean);
      const isVisible = !hasActiveFilter || termSlugs.includes(this.activeFilter);
      item.classList.toggle("hidden", !isVisible);
      if (isVisible) visibleCount += 1;
    });

    if (this.gridEmptyState) {
      this.gridEmptyState.classList.toggle("hidden", visibleCount > 0);
    }
  }

  emitUpdate() {
    window.dispatchEvent(
      new CustomEvent(PROJECT_FILTER_UPDATED_EVENT, {
        detail: {
          filter: this.activeFilter,
        },
      }),
    );
  }
}
